import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, X, MessageCircle } from "lucide-react";
import api from "@/lib/api";

const genId = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const WELCOME = "Ciao, sono Glowy ✨ la consulente di Skin Lovers. Sono qui per aiutarti a scoprire il tuo Glow Score gratuito e a trovare la routine giusta per la tua pelle. Dimmi pure come posso aiutarti 🌿";
const TEASER = "Ciao! Sono Glowy ✨ Vuoi il tuo Glow Score gratuito?";
const CHIPS = ["Che cos'è il Glow Score?", "Ho la pelle mista", "Consigli anti-età", "Spedizione e pagamenti"];

// Rende leggibili grassetti e link markdown nelle risposte di Glowy
const renderRich = (text, onDark = false, keyPrefix = "r") => {
  const out = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(String(text || "")))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    const linkCls = onDark ? "underline decoration-white/50 font-medium" : "underline text-[#C17F59] font-medium";
    if (m[1]) out.push(<strong key={key}>{renderRich(m[1], onDark, key)}</strong>);
    else if (m[2]) out.push(<a key={key} href={m[3]} target="_blank" rel="noopener noreferrer" className={linkCls}>{m[2]}</a>);
    else out.push(<a key={key} href={m[4]} target="_blank" rel="noopener noreferrer" className={linkCls}>vedi prodotto</a>);
    last = re.lastIndex;
  }
  if (last < String(text || "").length) out.push(text.slice(last));
  return out;
};

export const Glowy = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const reportId = useMemo(() => {
    const m = location.pathname.match(/^\/r\/(.+)$/);
    return m ? m[1] : null;
  }, [location.pathname]);

  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    const k = "glowy_session_id";
    let v = localStorage.getItem(k);
    if (!v) { v = genId(); localStorage.setItem(k, v); }
    return v;
  });
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const scrollRef = useRef(null);
  const recoveryRef = useRef(null);
  const engagedRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (isAdmin) return;
    const t = setTimeout(() => {
      if (!open && !sessionStorage.getItem("glowy_teaser_shown")) setTeaser(true);
    }, 3500);
    return () => clearTimeout(t);
  }, [isAdmin, open]);

  const openChat = () => { setOpen(true); setTeaser(false); sessionStorage.setItem("glowy_teaser_shown", "1"); };
  const dismissTeaser = (e) => { e.stopPropagation(); setTeaser(false); sessionStorage.setItem("glowy_teaser_shown", "1"); };

  useEffect(() => {
    if (isAdmin) return;
    if (localStorage.getItem("glowy_autoopened")) return;
    const onScroll = () => {
      if (window.scrollY > 600 && !localStorage.getItem("glowy_autoopened")) {
        setOpen(true);
        setTeaser(false);
        localStorage.setItem("glowy_autoopened", "1");
        sessionStorage.setItem("glowy_teaser_shown", "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onReady = async (e) => {
      const rid = e?.detail?.reportId;
      if (!rid || sessionStorage.getItem(`glowy_intro_${rid}`)) return;
      sessionStorage.setItem(`glowy_intro_${rid}`, "1");
      sessionStorage.setItem("glowy_teaser_shown", "1");
      localStorage.setItem("glowy_autoopened", "1");
      setTeaser(false);
      setTimeout(() => setOpen(true), 1200);
      setLoading(true);
      try {
        const { data } = await api.post("/glowy/intro", { session_id: sessionId, report_id: rid });
        if (data?.reply) setMessages([{ role: "assistant", content: data.reply }]);
      } catch (err) { console.warn("Glowy intro fallita:", err); /* resta il benvenuto */ }
      finally { setLoading(false); }

      // Recupero soft: dopo un bel po' (~5 min) chiediamo un parere, SENZA spingere il downsell
      if (!sessionStorage.getItem(`glowy_recovery_${rid}`)) {
        recoveryRef.current = setTimeout(() => {
          if (engagedRef.current) return;
          sessionStorage.setItem(`glowy_recovery_${rid}`, "1");
          const nudge = "Allora, com'è andata? 🌸 Hai avuto modo di leggere con calma il tuo Glow Score e le routine consigliate? Mi piacerebbe sapere cosa ne pensi — se qualcosa non ti è chiaro o vuoi un parere su quale pacchetto è più adatto a te, sono qui per aiutarti, senza fretta 💛";
          setMessages((m) => [...m, { role: "assistant", content: nudge }]);
          setTeaser(false);
          setOpen(true);
        }, 300000);
      }
    };
    window.addEventListener("glowy:report-ready", onReady);
    const onCart = () => { engagedRef.current = true; if (recoveryRef.current) clearTimeout(recoveryRef.current); };
    window.addEventListener("glowy:cart-click", onCart);
    if (window.__glowyReportReady) onReady({ detail: { reportId: window.__glowyReportReady } });
    return () => {
      window.removeEventListener("glowy:report-ready", onReady);
      window.removeEventListener("glowy:cart-click", onCart);
      if (recoveryRef.current) clearTimeout(recoveryRef.current);
    };
  }, [sessionId]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const focusHint = localStorage.getItem("glow_focus_concern") || "";
      const { data } = await api.post("/glowy/chat", { session_id: sessionId, message: content, report_id: reportId, focus_hint: focusHint });
      if (data?.focus) localStorage.setItem("glow_focus_concern", data.focus);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Ops, non riesco a rispondere ora. Riprova tra poco 🌿";
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  if (isAdmin) return null;

  return (
    <>
      {/* Bolla */}
      <AnimatePresence>
        {!open && (
          <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[60] flex flex-col items-end gap-3"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            {teaser && (
              <motion.div
                data-testid="glowy-teaser"
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                onClick={openChat}
                className="relative max-w-[70vw] sm:max-w-[240px] cursor-pointer rounded-2xl rounded-br-md bg-white px-4 py-3 text-sm text-[#2D2825] shadow-xl border border-[#E8E6E1]"
              >
                <button data-testid="glowy-teaser-close" onClick={dismissTeaser}
                  className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#2D2825] text-white" aria-label="Chiudi">
                  <X className="h-3 w-3" />
                </button>
                {TEASER}
              </motion.div>
            )}
            <motion.button
              data-testid="glowy-launcher"
              onClick={openChat}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 rounded-full p-3 sm:pl-3 sm:pr-5 text-white shadow-xl"
              style={{ background: "linear-gradient(135deg,#C17F59,#a96a47)" }}
              aria-label="Apri la chat con Glowy"
            >
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <Sparkles className="h-5 w-5" />
                <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#8BA888] border-2 border-white" />
              </span>
              <span className="hidden sm:inline font-display text-lg leading-none">Glowy</span>
            </motion.button>
          </div>
        )}
      </AnimatePresence>

      {/* Pannello chat */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="glowy-panel"
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-4 right-4 z-[60] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl bg-[#FAF9F6] shadow-2xl"
            style={{ height: "min(70vh, 560px)", border: "1px solid #E8E6E1" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: "linear-gradient(135deg,#C17F59,#a96a47)" }}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-xl leading-none">Glowy</p>
                  <p className="text-[11px] text-white/80">Consulente Skin Lovers · online</p>
                </div>
              </div>
              <button data-testid="glowy-close" onClick={() => setOpen(false)} className="rounded-full p-1.5 hover:bg-white/15" aria-label="Chiudi chat">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messaggi */}
            <div ref={scrollRef} data-testid="glowy-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={m.at || `${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`glowy-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-md bg-[#2D2825] text-white"
                        : "rounded-bl-md bg-white text-[#2D2825] border border-[#E8E6E1]"
                    }`}
                  >
                    {renderRich(m.content, m.role === "user")}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-[#E8E6E1] bg-white px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <motion.span key={d} className="h-2 w-2 rounded-full bg-[#C17F59]"
                        animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Suggerimenti rapidi (solo all'inizio) */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((c) => (
                    <button key={c} data-testid="glowy-chip" onClick={() => send(c)}
                      className="rounded-full border border-[#C17F59]/40 bg-white px-3 py-1.5 text-xs text-[#a96a47] hover:bg-[#C17F59]/10 transition-colors">
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#E8E6E1] bg-white p-3">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="glowy-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder="Scrivi a Glowy…"
                  className="max-h-24 flex-1 resize-none rounded-2xl border border-[#E8E6E1] bg-[#FAF9F6] px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]"
                />
                <button
                  data-testid="glowy-send"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C17F59] text-white hover:bg-[#a96a47] disabled:opacity-40"
                  aria-label="Invia"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-1.5 text-center text-[10px] text-[#B9B4AE]">Glowy è un'assistente AI · non sostituisce un consulto dermatologico</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Glowy;
